/**
 * Transaction Excel Import Service
 * 
 * Handles importing transactions from Excel format (.xlsx)
 */

import * as XLSX from 'xlsx';
import { supabase } from '../../../lib/supabase';
import { generateTransactionHash } from './export.service';

export interface ImportExcelOptions {
  skipDuplicates: boolean;
  autoCreateMasterData: boolean;
}

export interface ImportExcelResult {
  success: number;
  skipped: number;
  errors: number;
  details: Array<{
    row: number;
    description: string;
    status: 'success' | 'skipped' | 'error';
    message?: string;
  }>;
}

/**
 * Parse Excel file
 */
export async function parseExcelFile(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        console.log('[Parse Excel] Iniciando leitura do arquivo:', file.name);
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        console.log('[Parse Excel] Abas disponíveis:', workbook.SheetNames);
        
        // Read "Lançamentos" sheet - try multiple variations
        let sheetName = workbook.SheetNames.find(
          name => name === 'Lançamentos' || name === 'Lancamentos' || name === 'lançamentos' || name === 'lancamentos'
        );
        
        // If still not found, try case-insensitive search
        if (!sheetName) {
          sheetName = workbook.SheetNames.find(
            name => name.toLowerCase().includes('lançamento') || name.toLowerCase().includes('lancamento')
          );
        }
        
        // If still not found, use first sheet if there's only one
        if (!sheetName && workbook.SheetNames.length === 1) {
          console.warn('[Parse Excel] Aba "Lançamentos" não encontrada, usando a primeira aba disponível:', workbook.SheetNames[0]);
          sheetName = workbook.SheetNames[0];
        }
        
        if (!sheetName) {
          console.error('[Parse Excel] ERRO: Planilha "Lançamentos" não encontrada');
          console.error('[Parse Excel] Abas encontradas:', workbook.SheetNames);
          throw new Error('Planilha "Lançamentos" não encontrada no arquivo. Abas disponíveis: ' + workbook.SheetNames.join(', ') + '. Certifique-se de ter uma aba chamada "Lançamentos".');
        }
        
        console.log('[Parse Excel] Usando aba:', sheetName);
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        console.log('[Parse Excel] Total de linhas lidas:', jsonData.length);
        if (jsonData.length > 0) {
          console.log('[Parse Excel] Primeira linha (exemplo):', jsonData[0]);
          console.log('[Parse Excel] Colunas detectadas:', Object.keys(jsonData[0]));
        }
        
        resolve(jsonData);
      } catch (error) {
        console.error('[Parse Excel] Erro ao processar:', error);
        reject(error instanceof Error ? error : new Error('Erro ao processar arquivo Excel'));
      }
    };
    
    reader.onerror = () => {
      console.error('[Parse Excel] Erro ao ler arquivo');
      reject(new Error('Erro ao ler arquivo'));
    };
    reader.readAsBinaryString(file);
  });
}

/**
 * Translate Portuguese values back to English
 */
/**
 * Traduz tipo de lançamento para valores válidos do banco de dados
 * IMPORTANTE: A CHECK constraint do banco APENAS aceita 'income' e 'expense'
 * NUNCA retorna 'transfer' pois causa erro 23514
 */
function translateEntryType(type: string | null | undefined): 'income' | 'expense' {
  // Validação inicial - retorna default se inválido
  if (!type || typeof type !== 'string') {
    return 'expense'; // Default seguro
  }

  // Normalizar para comparação
  const normalized = type.trim().toLowerCase();

  // Mapeamento direto (case-insensitive)
  // ⚠️ NÃO incluir 'transfer' - a constraint do banco não aceita!
  const translations: Record<string, 'income' | 'expense'> = {
    // Português
    'receita': 'income',
    'despesa': 'expense',
    // Inglês
    'income': 'income',
    'expense': 'expense',
    // Variações
    'entrada': 'income',
    'saída': 'expense',
    'saida': 'expense',
  };
  
  // Tentar mapeamento direto
  const direct = translations[normalized];
  if (direct) return direct;
  
  // Tentar match parcial
  if (normalized.includes('receit') || normalized.includes('income') || normalized.includes('entrad')) {
    return 'income';
  }
  if (normalized.includes('despes') || normalized.includes('expense') || normalized.includes('said') || normalized.includes('gast')) {
    return 'expense';
  }
  
  // ⚠️ Transferências e outros tipos → classificar como 'expense'
  // Isso evita erro 23514 (check constraint violation)
  if (normalized.includes('transf') || normalized.includes('reembol')) {
    console.warn('[translateEntryType] Tipo "transfer/reembolso" não suportado pela constraint, usando "expense":', type);
    return 'expense';
  }
  
  // Default final (SEMPRE retorna um valor válido)
  console.warn('[translateEntryType] Tipo não reconhecido, usando default "expense":', type);
  return 'expense';
}

function translatePaymentStatus(status: string): string {
  const translations: Record<string, string> = {
    'Pago': 'paid',
    'Pendente': 'pending',
    'Vencido': 'overdue',
    'Previsão': 'previsão',
    'Confirmado': 'confirmado',
  };
  return translations[status] || status.toLowerCase();
}

/**
 * Parse Brazilian date format (DD/MM/YYYY) to ISO
 */
function parseDate(dateStr: string | number): string | null {
  if (!dateStr) return null;
  
  // If it's already a number (Excel serial date), convert it
  if (typeof dateStr === 'number') {
    const date = XLSX.SSF.parse_date_code(dateStr);
    return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
  }
  
  // If it's a string, try to parse DD/MM/YYYY
  const parts = String(dateStr).split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return null;
}

/**
 * Extract unique master data from all rows
 */
function extractUniqueMasterData(rows: any[]): {
  categories: Set<{ name: string; entry_type: string }>;
  subcategories: Set<{ name: string; main_category: string }>;
  paymentMethods: Set<string>;
  bankAccounts: Set<{ account_name: string; currency: string }>;
  businessPartners: Set<string>;
  costCenters: Set<string>;
  locations: Set<string>;
  documentSources: Set<string>;
} {
  const categories = new Map<string, { name: string; entry_type: string }>();
  const subcategories = new Map<string, { name: string; main_category: string }>();
  const paymentMethods = new Set<string>();
  const bankAccounts = new Map<string, { account_name: string; currency: string }>();
  const businessPartners = new Set<string>();
  const costCenters = new Set<string>();
  const locations = new Set<string>();
  const documentSources = new Set<string>();

  rows.forEach((row, index) => {
    // Categories (key: name, value includes entry_type)
    const categoryName = row['Categoria Principal'];
    const tipoRaw = row['Tipo'];
    const entryType = translateEntryType(tipoRaw);
    
    // Debug nas primeiras 3 linhas
    if (index < 3) {
      console.log(`[extractUniqueMasterData] Linha ${index + 1}:`, {
        categoryName,
        tipoRaw,
        entryType,
      });
    }
    
    if (categoryName && entryType) {
      const key = `${categoryName}|${entryType}`;
      if (!categories.has(key)) {
        categories.set(key, { name: categoryName, entry_type: entryType });
      }
    }

    // Subcategories (key: name + main_category)
    const subcategoryName = row['Subcategoria'];
    if (subcategoryName && categoryName) {
      const key = `${subcategoryName}|${categoryName}`;
      if (!subcategories.has(key)) {
        subcategories.set(key, { name: subcategoryName, main_category: categoryName });
      }
    }

    // Simple string fields
    const paymentMethod = row['Método Pagamento'];
    if (paymentMethod) paymentMethods.add(paymentMethod);

    const businessPartner = row['Parceiro'];
    if (businessPartner) businessPartners.add(businessPartner);

    const costCenter = row['Centro Custo'];
    if (costCenter) costCenters.add(costCenter);

    const location = row['Localização'];
    if (location) locations.add(location);

    const documentSource = row['Fonte Documento'];
    if (documentSource) documentSources.add(documentSource);

    // Bank accounts (key: account_name, includes currency)
    const accountName = row['Nome Conta'];
    const currency = row['Moeda'] || 'BRL';
    if (accountName && !bankAccounts.has(accountName)) {
      bankAccounts.set(accountName, { account_name: accountName, currency });
    }
  });

  return {
    categories: new Set(categories.values()),
    subcategories: new Set(subcategories.values()),
    paymentMethods,
    bankAccounts: new Set(bankAccounts.values()),
    businessPartners,
    costCenters,
    locations,
    documentSources,
  };
}

/**
 * Create all unique master data in batch (INSERT com ignorar duplicatas)
 */
async function createUniqueMasterData(
  masterData: ReturnType<typeof extractUniqueMasterData>,
  companyId: string,
  userId: string
): Promise<void> {
  console.log('[createUniqueMasterData] Creating unique master data:', {
    categories: masterData.categories.size,
    subcategories: masterData.subcategories.size,
    paymentMethods: masterData.paymentMethods.size,
    bankAccounts: masterData.bankAccounts.size,
    businessPartners: masterData.businessPartners.size,
    costCenters: masterData.costCenters.size,
    locations: masterData.locations.size,
    documentSources: masterData.documentSources.size,
  });

  let created = 0;
  let skipped = 0;

  // ✅ Com UNIQUE constraints no banco, usamos .ignoreDuplicates para INSERT silencioso
  
  // 1. Categories - INSERT com ignoreDuplicates
  if (masterData.categories.size > 0) {
    const categoriesToInsert = Array.from(masterData.categories).map(cat => ({
      company_id: companyId,
      user_id: userId,
      name: cat.name,
      entry_type: cat.entry_type,
    }));
    
    const { data, error } = await supabase
      .from('categories')
      .insert(categoriesToInsert)
      .select();
    
    if (data) created += data.length;
    if (error && error.code !== '23505') {
      console.warn('[createUniqueMasterData] Categories warning:', error.message);
    }
    skipped += categoriesToInsert.length - (data?.length || 0);
  }

  // 2. Subcategories - INSERT em batch
  if (masterData.subcategories.size > 0) {
    const subcategoriesToInsert = Array.from(masterData.subcategories).map(sub => ({
      company_id: companyId,
      user_id: userId,
      name: sub.name,
      main_category: sub.main_category,
    }));
    
    const { data } = await supabase.from('subcategories').insert(subcategoriesToInsert).select();
    created += data?.length || 0;
    skipped += subcategoriesToInsert.length - (data?.length || 0);
  }

  // 3. Payment Methods - INSERT em batch
  if (masterData.paymentMethods.size > 0) {
    const methodsToInsert = Array.from(masterData.paymentMethods).map(method => ({
      company_id: companyId,
      user_id: userId,
      name: method,
    }));
    
    const { data } = await supabase.from('payment_methods').insert(methodsToInsert).select();
    created += data?.length || 0;
    skipped += methodsToInsert.length - (data?.length || 0);
  }

  // 4. Bank Accounts - INSERT em batch
  if (masterData.bankAccounts.size > 0) {
    const accountsToInsert = Array.from(masterData.bankAccounts).map(account => ({
      company_id: companyId,
      user_id: userId,
      account_name: account.account_name,
      currency: account.currency,
    }));
    
    const { data } = await supabase.from('bank_accounts').insert(accountsToInsert).select();
    created += data?.length || 0;
    skipped += accountsToInsert.length - (data?.length || 0);
  }

  // 5. Business Partners - INSERT em batch
  if (masterData.businessPartners.size > 0) {
    const partnersToInsert = Array.from(masterData.businessPartners).map(partner => ({
      company_id: companyId,
      user_id: userId,
      name: partner,
    }));
    
    const { data } = await supabase.from('business_partners').insert(partnersToInsert).select();
    created += data?.length || 0;
    skipped += partnersToInsert.length - (data?.length || 0);
  }

  // 6. Cost Centers - INSERT em batch
  if (masterData.costCenters.size > 0) {
    const centersToInsert = Array.from(masterData.costCenters).map(center => ({
      company_id: companyId,
      user_id: userId,
      name: center,
    }));
    
    const { data } = await supabase.from('cost_centers').insert(centersToInsert).select();
    created += data?.length || 0;
    skipped += centersToInsert.length - (data?.length || 0);
  }

  // 7. Locations - INSERT em batch
  if (masterData.locations.size > 0) {
    const locationsToInsert = Array.from(masterData.locations).map(loc => ({
      company_id: companyId,
      user_id: userId,
      name: loc,
    }));
    
    const { data } = await supabase.from('locations').insert(locationsToInsert).select();
    created += data?.length || 0;
    skipped += locationsToInsert.length - (data?.length || 0);
  }

  // 8. Document Sources - INSERT em batch
  if (masterData.documentSources.size > 0) {
    const sourcesToInsert = Array.from(masterData.documentSources).map(source => ({
      company_id: companyId,
      user_id: userId,
      name: source,
    }));
    
    const { data } = await supabase.from('document_sources').insert(sourcesToInsert).select();
    created += data?.length || 0;
    skipped += sourcesToInsert.length - (data?.length || 0);
  }

  console.log(`[createUniqueMasterData] ✅ Concluído: ${created} criados, ${skipped} já existiam`);
}

/**
 * Auto-create master data if it doesn't exist (DEPRECATED - dados mestres agora são criados em batch)
 * Esta função não faz mais nada, pois os dados mestres são criados antes em createUniqueMasterData()
 */
async function ensureMasterDataExists(
  transactionData: any,
  companyId: string,
  userId: string
): Promise<void> {
  // ✅ DEPRECATED: Dados mestres agora são criados em batch antes de processar lançamentos
  // Esta função é mantida apenas para compatibilidade, mas não executa nada
  return Promise.resolve();
}

/**
 * Check if transaction exists by hash
 */
async function findExistingTransactionByHash(
  hash: string,
  companyId: string
): Promise<string | null> {
  const { data: transactions } = await supabase
    .from('cash_flow_entries')
    .select('id, company_id, description, amount, entry_date, due_date, payment_date, invoice_number, category, subcategory, cost_center, payment_method, business_partner')
    .eq('company_id', companyId);

  if (!transactions) return null;

  for (const transaction of transactions) {
    const existingHash = await generateTransactionHash({
      company_id: transaction.company_id,
      description: transaction.description,
      amount: transaction.amount,
      entry_date: transaction.entry_date,
      due_date: transaction.due_date,
      payment_date: transaction.payment_date,
      invoice_number: transaction.invoice_number,
      category: transaction.category,
      subcategory: transaction.subcategory,
      cost_center: transaction.cost_center,
      payment_method: transaction.payment_method,
      business_partner: transaction.business_partner,
    });

    if (existingHash === hash) {
      return transaction.id;
    }
  }

  return null;
}

/**
 * Import single row from Excel
 */
async function importSingleRow(
  row: any,
  rowIndex: number,
  userId: string,
  companyId: string,
  options: ImportExcelOptions
): Promise<{
  status: 'success' | 'skipped' | 'error';
  message?: string;
}> {
  try {
    if (rowIndex <= 3) {
      console.log(`[Import Excel] 📝 Linha ${rowIndex}:`, JSON.stringify(row, null, 2));
    } else if (rowIndex === 4) {
      console.log(`[Import Excel] ... (linhas 4-${rowIndex} omitidas para reduzir logs)`);
    }
    
    // Map ALL Excel columns to database fields (36 campos completos)
    const transactionData = {
      // IDs
      company_id: row['Empresa ID'] || companyId,
      user_id: row['User ID'] || userId,
      
      // Informações básicas
      description: row['Descrição'],
      notes: row['Observações'] || null,
      amount: typeof row['Valor'] === 'string' 
        ? parseFloat(row['Valor'].replace(',', '.')) 
        : row['Valor'],
      currency: row['Moeda'] || 'BRL',
      entry_type: translateEntryType(row['Tipo']),
      
      // Categorização completa
      main_category: row['Categoria Principal'] || null,
      category: row['Categoria'] || row['Categoria Principal'] || 'Outros', // Usa Categoria Principal como fallback
      subcategory: row['Subcategoria'] || null,
      classification: row['Classificação'] || null,
      budget_category: row['Categoria Orçamentária'] || null,
      expense_type: row['Tipo Despesa'] || null,
      cost_center: row['Centro de Custo'] || null,
      
      // Datas
      entry_date: parseDate(row['Data Lançamento']),
      due_date: parseDate(row['Data Vencimento']),
      payment_date: parseDate(row['Data Pagamento']),
      
      // Recorrência
      is_recurring: row['Recorrente'] === 'Sim',
      frequency: row['Frequência'] || null,
      recurrence_end_date: parseDate(row['Data Fim Recorrência']),
      recurrence_group_id: row['Grupo Recorrência'] || null,
      
      // Pagamento
      payment_status: translatePaymentStatus(row['Status Pagamento'] || 'previsão'),
      payment_method: row['Método Pagamento'] || null,
      account_name: row['Nome Conta'] || null,
      payment_details: row['Detalhes Pagamento'] || null,
      
      // Informações adicionais
      invoice_number: row['Nº Documento'] || null,
      tax_category: row['Categoria Fiscal'] || null,
      business_partner: row['Parceiro'] || null,
      location: row['Localização'] || null,
      stakeholders: row['Stakeholders'] || null,
      document_source: row['Fonte Documento'] || null,
      tags: row['Tags'] ? row['Tags'].split(',').map((t: string) => t.trim()) : null,
      
      // Controle
      is_simulation: row['Simulação'] === 'Sim',
    };

    console.log(`[Import Excel] Linha ${rowIndex} - dados processados:`, {
      description: transactionData.description,
      amount: transactionData.amount,
      entry_type: transactionData.entry_type,
      entry_date: transactionData.entry_date,
    });

    // Validate required fields
    if (!transactionData.description) {
      console.error(`[Import Excel] Linha ${rowIndex} - ERRO: Descrição vazia`);
      return {
        status: 'error',
        message: 'Descrição é obrigatória',
      };
    }

    if (!transactionData.amount || isNaN(transactionData.amount)) {
      console.error(`[Import Excel] Linha ${rowIndex} - ERRO: Valor inválido:`, row['Valor']);
      return {
        status: 'error',
        message: `Valor inválido: ${row['Valor']}`,
      };
    }

    // ✅ Dados mestres já foram criados em batch antes do loop de importação
    // Check for duplicates
    if (options.skipDuplicates) {
      const hash = await generateTransactionHash({
        company_id: transactionData.company_id,
        description: transactionData.description,
        amount: transactionData.amount,
        entry_date: transactionData.entry_date,
        due_date: transactionData.due_date,
        payment_date: transactionData.payment_date,
        invoice_number: transactionData.invoice_number,
        category: transactionData.category,
        subcategory: transactionData.subcategory,
        cost_center: transactionData.cost_center,
        payment_method: transactionData.payment_method,
        business_partner: transactionData.business_partner,
      });

      const existingId = await findExistingTransactionByHash(hash, transactionData.company_id);
      
      if (existingId) {
        return {
          status: 'skipped',
          message: 'Lançamento já existe',
        };
      }
    }

    // Check if it's an update (has ID)
    const existingId = row['ID'];
    
    if (existingId) {
      console.log(`[Import Excel] Linha ${rowIndex} - Atualizando lançamento existente ID: ${existingId}`);
      console.log(`[Import Excel] Linha ${rowIndex} - Dados a serem atualizados:`, JSON.stringify(transactionData, null, 2));
      
      // Update existing transaction
      const { error } = await supabase
        .from('cash_flow_entries')
        .update(transactionData)
        .eq('id', existingId);

      if (error) {
        console.error(`[Import Excel] Linha ${rowIndex} - ERRO COMPLETO ao atualizar:`);
        console.error(`  - Code: ${error.code}`);
        console.error(`  - Message: ${error.message}`);
        console.error(`  - Details: ${error.details}`);
        console.error(`  - Hint: ${error.hint}`);
        console.error(`  - Erro completo:`, JSON.stringify(error, null, 2));
        throw error;
      }

      console.log(`[Import Excel] Linha ${rowIndex} - Atualizado com sucesso`);
      return {
        status: 'success',
        message: 'Atualizado',
      };
    } else {
      console.log(`[Import Excel] Linha ${rowIndex} - Criando novo lançamento`);
      console.log(`[Import Excel] Linha ${rowIndex} - Dados a serem inseridos:`, JSON.stringify(transactionData, null, 2));
      
      // Create new transaction
      const { error, data } = await supabase
        .from('cash_flow_entries')
        .insert(transactionData)
        .select();

      if (error) {
        console.error(`[Import Excel] Linha ${rowIndex} - ERRO COMPLETO ao inserir:`);
        console.error(`  - Code: ${error.code}`);
        console.error(`  - Message: ${error.message}`);
        console.error(`  - Details: ${error.details}`);
        console.error(`  - Hint: ${error.hint}`);
        console.error(`  - Erro completo:`, JSON.stringify(error, null, 2));
        throw error;
      }

      if (rowIndex <= 5 || rowIndex % 50 === 0) {
        console.log(`[Import Excel] ✅ Linha ${rowIndex} - Criado com sucesso, ID:`, data?.[0]?.id);
      }
      return {
        status: 'success',
        message: 'Importado',
      };
    }
  } catch (error) {
    console.error(`[Import Excel] Linha ${rowIndex} - ERRO ao importar:`, error);
    
    // Extrair mensagem de erro mais específica
    let errorMessage = 'Erro desconhecido';
    if (error && typeof error === 'object') {
      const err = error as any;
      if (err.message) errorMessage = err.message;
      if (err.hint) errorMessage += ` (Dica: ${err.hint})`;
      if (err.details) errorMessage += ` (Detalhes: ${err.details})`;
    }
    
    return {
      status: 'error',
      message: errorMessage,
    };
  }
}

/**
 * Import transactions from Excel file
 */
export async function importTransactionsFromExcel(
  file: File,
  userId: string,
  companyId: string,
  options: ImportExcelOptions,
  onProgress?: (progress: { current: number; total: number; message: string }) => void
): Promise<ImportExcelResult> {
  const result: ImportExcelResult = {
    success: 0,
    skipped: 0,
    errors: 0,
    details: [],
  };

  try {
    // Parse file
    onProgress?.({ current: 0, total: 0, message: 'Lendo arquivo Excel...' });
    const rows = await parseExcelFile(file);

    if (rows.length === 0) {
      throw new Error('Nenhum lançamento encontrado no arquivo');
    }

    const total = rows.length;
    console.log(`[Import Excel] Total de linhas: ${total}`);

    // ✅ PASSO 1: Extrair e criar dados mestres únicos ANTES de processar lançamentos
    onProgress?.({ current: 0, total: total, message: 'Criando dados mestres...' });
    const uniqueMasterData = extractUniqueMasterData(rows);
    await createUniqueMasterData(uniqueMasterData, companyId, userId);
    console.log('[Import Excel] ✅ Dados mestres únicos criados com sucesso');

    // ✅ PASSO 2: Importar cada linha (lançamentos)
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowIndex = i + 2; // +2 because Excel starts at 1 and has header
      
      // Report progress
      onProgress?.({
        current: i + 1,
        total: total,
        message: `Importando linha ${i + 1} de ${total}...`,
      });

      const importResult = await importSingleRow(
        row,
        rowIndex,
        userId,
        companyId,
        options
      );

      result.details.push({
        row: rowIndex,
        description: row['Descrição'] || `Linha ${rowIndex}`,
        status: importResult.status,
        message: importResult.message,
      });

      if (importResult.status === 'success') {
        result.success++;
      } else if (importResult.status === 'skipped') {
        result.skipped++;
      } else {
        result.errors++;
      }
    }

    // Final progress
    onProgress?.({
      current: total,
      total: total,
      message: 'Importação concluída!',
    });

    console.log('[Import Excel] 📊 RESUMO FINAL:', {
      total: total,
      success: result.success,
      skipped: result.skipped,
      errors: result.errors,
      successRate: `${((result.success / total) * 100).toFixed(1)}%`,
    });

    return result;
  } catch (error) {
    console.error('[Import Excel Service] Error:', error);
    throw error;
  }
}

